<template>
  <div id="app">
    <ChatContainer
      :messages="messages"
      :is-typing="isTyping"
      @send-message="handleSendMessage"
      @clear-chat="clearChat"
      @show-temp-message="showTempMessage"
      @update-rating="handleUpdateRating"
      @language-changed="handleLanguageChange" 
    />
  </div>
</template>

<script>
import ChatContainer from './components/ChatContainer.vue'
import { chatAPI } from './services/api'
//import { frontendLLMService } from './services/frontendLLMService'

const DEFAULT_WELCOME_MESSAGE = {
  de: 'Hallo! Ich bin Ihr Schuberth-Assistant. Wie kann ich Ihnen heute helfen?',
  en: 'Hello! I am your Schuberth assistant. How can I help you today?',
  fr: 'Bonjour ! Je suis votre assistant Schuberth. Comment puis-je vous aider?',
  it: 'Ciao! Sono il tuo assistente Schuberth. Come posso aiutarti?',
  es: '¡Hola! Soy tu asistente Schuberth. ¿Cómo puedo ayudarte?',
  pt: 'Olá! Sou seu assistente Schuberth. Como posso ajudá-lo?',
  pl: 'Cześć! Jestem Twoim asystentem Schuberth. Jak mogę Ci pomóc?',

}

const DEFAULT_RESET_MESSAGE = {
  de: 'Hallo! Ich bin Ihr Schuberth-Assistent. Der Chat wurde zurückgesetzt. Wie kann ich Ihnen heute helfen?',
  en: 'Hello! I am your Schuberth assistant. The chat has been reset. How can I help you?',
  fr: 'Bonjour ! Je suis votre assistant Schuberth. Le chat a été réinitialisé. Comment puis-je vous aider ?',
  it: 'Ciao! Sono il tuo assistente Schuberth. La chat è stata ripristinata. Come posso aiutarti?',
  es: '¡Hola! Soy tu asistente Schuberth. El chat ha sido reiniciado. ¿Cómo puedo ayudarte?',
  pt: 'Olá! Sou seu assistente Schuberth. O chat foi redefinido. Como posso ajudá-lo?',
  pl: 'Cześć! Jestem Twoim asystentem Schuberth. Czat został zresetowany. Jak mogę Ci pomóc?',

}

export default {
  name: 'App',
  components: {
    ChatContainer
  },
  data() {
    return {
      messages: [],
      isTyping: false,
      selectedLanguage: 'de',
      sessionId: null
    }
  },
  methods: {
    handleLanguageChange(language) {
      this.selectedLanguage = language
    },

    async getWelcomeMessage(lang) {
      // Fallback auf vordefinierte Nachrichten
      return DEFAULT_WELCOME_MESSAGE[lang] || DEFAULT_WELCOME_MESSAGE.de
    },

    async getResetMessage(lang) {
      // Fallback auf vordefinierte Nachrichten
      return DEFAULT_RESET_MESSAGE[lang] || DEFAULT_RESET_MESSAGE.de
    },

    sanitizeContent(content) {
      if (!content) return '';

      // ── Schritt 1: Iframes und YouTube-Wrapper schützen ─────────────────────
      // sanitizeContent darf width/height der iframes NICHT entfernen,
      // sonst werden YouTube-Videos unsichtbar (width=0).
      const protectedBlocks = [];
      let safe = content.replace(
        /<iframe[\s\S]*?<\/iframe>/gi,
        (match) => {
          protectedBlocks.push(match);
          return `__PROTECTED_BLOCK_${protectedBlocks.length - 1}__`;
        }
      );

      // ── Schritt 2: Normale Bereinigung ──────────────────────────────────────
      let sanitized = safe
        .replace(/width\s*:\s*\d+px/gi, '')
        .replace(/width\s*=\s*["']\d+["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*width[^"']*["']/gi, '')
        .replace(/<table/gi, '<table style="width:100%;table-layout:fixed;"')
        // <img nur ergänzen wenn noch kein style-Attribut vorhanden
        .replace(/<img(?![^>]*style=)/gi, '<img style="max-width:100%;height:auto;"');

      // ── Schritt 3: Iframes wiederherstellen ─────────────────────────────────
      protectedBlocks.forEach((block, i) => {
        sanitized = sanitized.replace(`__PROTECTED_BLOCK_${i}__`, block);
      });

      return sanitized;
    },

async handleSendMessage(message, sessionId, topic, userLang, chatSource) {
  if (userLang && userLang !== this.selectedLanguage) {
    this.selectedLanguage = userLang
  }

  const userMessage = {
    id: Date.now(),
    type: 'user',
    content: message,
    timestamp: new Date()
  }
  this.messages.push(userMessage)
  this.isTyping = true
  
  try {
    const response = await chatAPI.sendMessage(
      message,
      5,
      sessionId || this.sessionId,
      topic,
      userLang || this.selectedLanguage
    )
    
    const botMessage = {
      id: Date.now() + 1,
      type: 'bot',
      content: this.sanitizeContent(response.response),
      timestamp: new Date(),
      sources: response.sources || []
    }
    this.messages.push(botMessage)
    
    // Frage UND Antwort zusammen in Cockpit speichern
    chatAPI.submitQAToCockpit(
      message,
      response.response,
      chatSource || 'chat',
      sessionId || this.sessionId,
      userLang || this.selectedLanguage
    );
    
  } catch (error) {
    console.error('Chat error:', error)
    
    const errorMessage = 'Es tut mir leid, ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.'
    
    this.messages.push({
      id: Date.now() + 1,
      type: 'bot',
      content: errorMessage,
      timestamp: new Date(),
      isError: true
    })
    
    // Auch Fehler speichern
    chatAPI.submitQAToCockpit(
      message,
      `ERROR: ${error.message}`,
      chatSource || 'chat_error',
      sessionId || this.sessionId,
      userLang || this.selectedLanguage
    );
    
  } finally {
    this.isTyping = false
  }
},

    async clearChat() {
      this.messages = []
      this.sessionId = Date.now().toString()
      
      this.$nextTick(async () => {
        const resetMessage = await this.getResetMessage(this.selectedLanguage)
        
        this.messages.push({
          id: 1,
          type: 'bot',
          content: resetMessage,
          timestamp: new Date()
        })
      })
    },

    handleUpdateRating({ messageId, rating }) {
      const messageIndex = this.messages.findIndex(msg => msg.id === messageId)
      if (messageIndex !== -1) {
        this.messages[messageIndex] = {
          ...this.messages[messageIndex],
          userRating: rating
        }
      }
    },

    showTempMessage(message) {
      const tempMessage = {
        id: Date.now(),
        type: 'bot',
        content: message,
        timestamp: new Date(),
        isStatus: true
      }

      this.messages.push(tempMessage)

      setTimeout(() => {
        const index = this.messages.findIndex(msg => msg.id === tempMessage.id)
        if (index !== -1) {
          this.messages.splice(index, 1)
        }
      }, 3000)
    }
  },
  async mounted() {
    const urlParams = new URLSearchParams(window.location.search)
    const lang = urlParams.get('lang') || urlParams.get('userlang')
    
    if (lang) {
      this.selectedLanguage = lang
      //console.log(`🌐 App: Sprache aus URL übernommen: ${lang}`)
    }

    this.sessionId = Date.now().toString()

    // Dynamische Willkommensnachricht
    const welcomeMessage = await this.getWelcomeMessage(this.selectedLanguage)
    
    this.messages.push({
      id: 1,
      type: 'bot',
      content: welcomeMessage,
      timestamp: new Date()
    })
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  --primary-color: #ee7100;
  --primary-dark: #91281e;
  --accent-color: #ee7100;
  --text-color: #2b2d42;
  --text-light: #6c757d;
  --background-light: #e7e7e7;
  --bg-color: #f8f9fa;
  --bg-dark: #121212;
  --card-bg: #ffffff;
  --card-bg-dark: #1e1e1e;
  --border-color: #dee2e6;
  --border-dark: #444;
  --success-color: #28a745;
  --error-color: #dc3545;
  --warning-color: #ffc107;
  --shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  --radius: 8px;

  --font-main: 'MyFont', 'Inter', sans-serif;
  --font-heading: 'MyFont', 'Montserrat', sans-serif;
}

[data-theme="dark"] {
  --text-color: #e0e0e0;
  --text-light: #a0a0a0;
  --bg-color: var(--bg-dark);
  --card-bg: var(--card-bg-dark);
  --border-color: var(--border-dark);
}

body {
  font-family: var(--font-main);
  background-color: var(--bg-color);
  color: var(--text-color);
  line-height: 1.6;
  min-height: 100vh;
  transition: background-color 0.3s, color 0.3s;
  padding-top: 70px;
}

#app {
  min-height: calc(100vh - (142.2px + 60px));
  display: flex;
  flex-direction: column;
}
</style>